import CONSTANT from './constants';
var InteractionHandler = /** @class */ (function () {
    function InteractionHandler(select) {
        var _this = this;
        this.select = select;
        this.focusListener = function (ev) {
            ev.stopPropagation();
            // We already have focus, probably through clicking the trigger
            if (_this.isFocused) {
                _this.skipNextFocus = false;
                return;
            }
            _this.isFocused = true;
            if (_this.skipNextFocus) {
                _this.skipNextFocus = false;
                return;
            }
            _this.select.__fireEvent('focus');
        };
        this.blurListener = function (ev) {
            ev.stopPropagation();
            if (_this.skipNextBlur) {
                _this.skipNextBlur = false;
                return;
            }
            _this.select.__fireEvent('blur');
            _this.isFocused = false;
            _this.skipNextFocus = false;
        };
        this.mouseupListener = function () {
            if (_this.select.__open && _this.select.controller.shouldDisplayFilter()) {
                _this.__focusFiltering();
                // It can happen that the user switched from keyboard interaction
                // back to navigating with the mouse
                _this.select.__highlightedOption = _this.select.controller.getDefaultHighlightedOption();
            }
        };
        this.toggleListener = function (ev) {
            ev.stopPropagation();
            if (_this.select.__open) {
                _this.select.controller.closeDropdown();
                _this.isFocused = false;
                _this.__focusTrigger();
            }
            else {
                _this.select.controller.openDropdown();
                if (!_this.isFocused) {
                    _this.select.__fireEvent('focus');
                    _this.isFocused = true;
                }
            }
        };
        this.selectionListener = function (ev) {
            ev.stopPropagation();
            // To be aligned with the keyboard behavior, we need to also re-focus the trigger when
            // selecting an option with the mouse. It is skipped to make sure that we do not raise an
            // unnecessary focus event.
            _this.skipNextFocus = true;
            // we need to focus before closing the dropdown, otherwise the focus might temporarily
            // leave the component and go back to the body.
            _this.select.focus();
            _this.select.controller.selectOption(ev.detail);
        };
        this.dismissListener = function (_a) {
            var detail = _a.detail;
            // "instanceof MultiselectController" can't be used here because it creates a circular dependency
            if (_this.select.controller.dismissOption) {
                // Focus on the control to keep the same behavior as before having the TokenGroup component
                // It should be removed in v2.2, https://issues.amazon.com/issues/AWSUI-6185
                _this.skipNextFocus = true;
                _this.select.focus();
                _this.select.controller.dismissOption(detail);
            }
        };
        this.keydownListener = function (ev) {
            var select = _this.select;
            var controller = _this.select.controller;
            switch (ev.keyCode) {
                case CONSTANT.SPACE:
                    if (!_this.select.__open || !controller.shouldDisplayFilter()) {
                        ev.preventDefault();
                        _this.__toggleSelect();
                    }
                    return;
                case CONSTANT.ENTER:
                    ev.preventDefault();
                    _this.__toggleSelect();
                    return;
                case CONSTANT.TAB:
                    controller.closeDropdown(!ev.shiftKey);
                    _this.isFocused = false;
                    _this.skipNextBlur = false;
                    _this.skipNextFocus = ev.shiftKey;
                    return;
                case CONSTANT.ESCAPE:
                    ev.preventDefault();
                    _this.skipNextFocus = true;
                    controller.closeDropdown();
                    select.focus();
                    return;
                case CONSTANT.UP:
                    ev.preventDefault();
                    select.__open ? _this.__moveHighlight(-1) : _this.__openDropdownAndHighlight();
                    return;
                case CONSTANT.DOWN:
                    ev.preventDefault();
                    select.__open ? _this.__moveHighlight(1) : _this.__openDropdownAndHighlight();
                    return;
            }
        };
        this.globalMousedownListener = function (ev) {
            if (!_this.select.__open) {
                return;
            }
            if (!_this.select.node.contains(ev.target)) {
                _this.select.controller.closeDropdown(true);
            }
            else {
                var dropdown = _this.select.node.querySelector('.awsui-select-dropdown');
                // Prevent any blurring that happens because of clicking inside the dropdown.
                // This also means that we do not fire a blur event after an option has been selected.
                // This matches the behavior of the native select.
                if (dropdown.contains(ev.target)) {
                    ev.preventDefault();
                }
            }
        };
    }
    InteractionHandler.prototype.addListeners = function () {
        var node = this.select.node;
        // Close the dropdown when the user clicks away
        document.addEventListener('mousedown', this.globalMousedownListener);
        node.addEventListener('mouseup', this.mouseupListener);
        // We need to wrap the native events with our own
        node.addEventListener(CONSTANT.AWSUI_PREFIX + "__triggerBlur", this.blurListener);
        node.addEventListener(CONSTANT.AWSUI_PREFIX + "__triggerFocus", this.focusListener);
        node.addEventListener(CONSTANT.AWSUI_PREFIX + "dismiss", this.dismissListener);
        node.addEventListener(CONSTANT.AWSUI_PREFIX + "__selected", this.selectionListener);
        // Toggle the dropdown when the trigger is clicked
        node.addEventListener(CONSTANT.AWSUI_PREFIX + "__toggle", this.toggleListener);
        // Handle keyboard interaction
        node.querySelector('.awsui-select-keyboard-area').addEventListener('keydown', this.keydownListener);
    };
    InteractionHandler.prototype.removeListeners = function () {
        var node = this.select.node;
        document.removeEventListener('mousedown', this.globalMousedownListener);
        node.removeEventListener(CONSTANT.AWSUI_PREFIX + "__triggerBlur", this.blurListener);
        node.removeEventListener(CONSTANT.AWSUI_PREFIX + "__triggerFocus", this.focusListener);
        node.querySelector('.awsui-select-keyboard-area').removeEventListener('keydown', this.keydownListener);
        node.removeEventListener('mouseup', this.mouseupListener);
        node.removeEventListener(CONSTANT.AWSUI_PREFIX + "dismiss", this.dismissListener);
        node.removeEventListener(CONSTANT.AWSUI_PREFIX + "__selected", this.selectionListener);
        node.removeEventListener(CONSTANT.AWSUI_PREFIX + "__toggle", this.toggleListener);
    };
    InteractionHandler.prototype.__focusFiltering = function () {
        if (document.activeElement === this.select.controller.getFilterInput()) {
            return;
        }
        this.skipNextBlur = true;
        this.select.controller.getFilterComponent().focus();
    };
    InteractionHandler.prototype.__focusDropdown = function () {
        this.skipNextBlur = true;
        this.select.controller.getDropdownComponent().focusDropdown();
    };
    InteractionHandler.prototype.__focusTrigger = function () {
        this.skipNextFocus = true;
        this.select.controller.getTriggerComponent().focus();
    };
    InteractionHandler.prototype.focusActiveElement = function () {
        if (document.activeElement === this.select.node.querySelector('.awsui-select-trigger')) {
            this.skipNextFocus = true;
        }
        if (this.select.controller.shouldDisplayFilter()) {
            this.__focusFiltering();
        }
        else {
            this.__focusDropdown();
        }
    };
    InteractionHandler.prototype.__openDropdownAndHighlight = function () {
        this.select.controller.openDropdown();
        this.select.__highlightedOption = this.select.controller.getDefaultHighlightedOption();
    };
    InteractionHandler.prototype.__moveHighlight = function (dir) {
        var options = this.select.controller.getEnabledOptions(true);
        var select = this.select;
        if (options.length === 0) {
            return;
        }
        var currIdx = options.indexOf(select.__highlightedOption);
        var nextIdx = currIdx + dir;
        if (options[nextIdx]) {
            select.__highlightedOption = options[nextIdx];
        }
    };
    InteractionHandler.prototype.__toggleSelect = function () {
        if (!this.select.__open) {
            this.__openDropdownAndHighlight();
            return;
        }
        if (!this.select.__highlightedOption) {
            if (!this.select.controller.shouldDisplayFilter()) {
                this.select.controller.closeDropdown();
            }
            return;
        }
        // we need to focus before closing the dropdown, otherwise the focus might temporarily
        // leave the component and go back to the body.
        this.skipNextFocus = true;
        if (!this.select.keepOpen) {
            this.select.focus();
        }
        this.select.controller.selectOption(this.select.__highlightedOption);
    };
    return InteractionHandler;
}());
export default InteractionHandler;
//# sourceMappingURL=interaction-handler.js.map